import { z } from "zod";
import { BriefingRequestSchema, HIRA_DETAIL_TTL_MS, RedFlagAnswersSchema, RelationSchema, type FacilityInformation, type HiraDetailsView, type PharmacyHoursView, type ServiceType } from "@predoc/domain";
import { koreanWeekday } from "./visit-preparation.js";

export const relationLabels = { SELF: "나", CHILD: "아이", ADULT_OTHER: "성인 가족·다른 사람", OLDER_ADULT: "어르신" } as const;
export const answerLabels = { YES: "예", NO: "아니요", UNKNOWN: "잘 모르겠어요" } as const;
export const IntakeSchema = z.object({
  relation: RelationSchema,
  ageMonths: z.number().int().min(0).max(1560).nullable(),
  onsetDescription: z.string().trim().min(1).max(80).nullable(),
  situation: BriefingRequestSchema.shape.freeText.unwrap().min(1),
  redFlags: RedFlagAnswersSchema,
});
export type Intake = z.infer<typeof IntakeSchema>;
// Only a validated search result can be selected. Health text never selects,
// invents or modifies an institution, phone number, opening time or URL.
export interface PreparationFacility {
  id: string; name: string; address: string; phone: string | null; emergencyPhone: string | null;
  evidence: Array<{ source: string; sourceUrl: string; fetchedAt: string }>;
  information?: FacilityInformation | undefined;
  details?: HiraDetailsView | undefined;
  pharmacyHours?: PharmacyHoursView | undefined;
}

export function ageLabel(ageMonths: number | null, unit: string): string {
  return ageMonths === null ? "모름 · 확인 필요" : unit === "YEARS" ? `${ageMonths / 12}세 (만 나이)` : `${ageMonths}개월`;
}

export function preparationPhone(facility: PreparationFacility): string | null {
  return [facility.emergencyPhone, facility.phone].find((phone) => phone !== null && /^\+?[0-9][0-9 ()-]{3,29}$/u.test(phone)) ?? null;
}

export function preparationTime(value: string | null): string {
  return value === null || !Number.isFinite(Date.parse(value)) ? "확인 시각 미제공" : new Intl.DateTimeFormat("ko-KR", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Seoul" }).format(new Date(value));
}

/** Re-age the selected result while the tab is open; never infer today's opening. */
export function preparationHours(facility: PreparationFacility, service: ServiceType, now: Date) {
  const pharmacy = service === "PHARMACY";
  const view = pharmacy ? facility.pharmacyHours : facility.details?.hours;
  const day = koreanWeekday(now);
  const fetchedAt = view?.fetchedAt ?? null;
  const age = fetchedAt === null ? NaN : now.getTime() - Date.parse(fetchedAt);
  const dayMs = 86_400_000;
  const usable = view !== undefined && ["CURRENT", "LAST_KNOWN"].includes(view.status) && Number.isFinite(age) && age >= 0 && (!pharmacy || age < 7 * dayMs);
  const entry = usable ? view.data?.weekly.find((entry) => entry.day === day) : undefined;
  const lastKnown = usable && (view.status === "LAST_KNOWN" || (pharmacy ? age >= dayMs : age > HIRA_DETAIL_TTL_MS));
  return {
    day, fetchedAt, lastKnown,
    time: entry === undefined ? null : `${entry.start}–${"nextDay" in entry && entry.nextDay ? "다음 날 " : ""}${entry.end}`,
    sourceUrl: pharmacy ? facility.pharmacyHours?.sourceUrl : facility.details?.sourceUrl,
  };
}
